import type { MouseEvent, ReactNode } from 'react';

/**
 * One clickable row in a list pane — the "My items" sidebar today (plan 2.4), Phase 3's
 * feed panel next, which is why this is a component rather than markup inlined where it is
 * first used: the plan names `ItemRow` as the shared piece and whoever lands first creates
 * it.
 *
 * Deliberately dumb: an optional leading visual (a node's colour dot, a doc-type icon), an
 * ellipsised label, and optional trailing controls (an un-star button, a timestamp). What a
 * click MEANS belongs to the caller. Styling is `.itemrow` in `styles/app.css`, modelled on
 * `.tagrow` so the two list families in the sidebar read as siblings.
 *
 * `label` TAKES A NODE, NOT ONLY A STRING (V0.45.2), because the bell's rows put a name inside
 * a sentence and that name carries its node's colour — which a string cannot express. A string
 * still behaves exactly as it always did, including as the default tooltip; a node has no text
 * to borrow, so a caller passing one owns its `title` too.
 */
export interface ItemRowProps {
  /** Leading visual — a colour dot, a type icon. Rendered as-is. */
  icon?: ReactNode;
  label: ReactNode;
  /**
   * Tooltip. Defaults to the label when the label is a plain string — which is usually
   * ellipsised and wants one. A composed label cannot be flattened into an attribute, so
   * pass the text version explicitly.
   */
  title?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Right-aligned extras. Handle their own clicks (and stop propagation). */
  trailing?: ReactNode;
  className?: string;
  /** `data-*` attributes for the row element, so a test can find a specific row. */
  data?: Record<string, string>;
}

export function ItemRow({ icon, label, title, onClick, trailing, className, data }: ItemRowProps) {
  return (
    <div
      className={className ? `itemrow ${className}` : 'itemrow'}
      title={title ?? (typeof label === 'string' ? label : undefined)}
      onClick={onClick}
      {...data}
    >
      {icon}
      <span className="name">{label}</span>
      {trailing}
    </div>
  );
}
